import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Bookmark, Save, Trash2, FolderOpen } from "lucide-react";

export function SavedFiltersBar({ module, currentFilters, onLoadFilter }) {
  const { axiosAuth } = useAuth();
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveDialog, setSaveDialog] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeFilterId, setActiveFilterId] = useState(null);

  const loadSavedFilters = useCallback(async () => {
    try {
      const r = await axiosAuth().get(`/saved-filters?module=${module}`);
      setSavedFilters(r.data.filters || []);
    } catch {}
  }, [axiosAuth, module]);

  useEffect(() => { loadSavedFilters(); }, [loadSavedFilters]);

  const handleSave = async () => {
    if (!filterName.trim()) { toast.error("Nombre obligatorio"); return; }
    setSaving(true);
    try {
      if (activeFilterId) {
        await axiosAuth().put(`/saved-filters/${activeFilterId}`, { name: filterName, filters: currentFilters });
        toast.success("Filtro actualizado");
      } else {
        const r = await axiosAuth().post("/saved-filters", { name: filterName, module, filters: currentFilters });
        setActiveFilterId(r.data.id);
        toast.success("Filtro guardado");
      }
      setSaveDialog(false);
      await loadSavedFilters();
    } catch { toast.error("Error al guardar filtro"); }
    finally { setSaving(false); }
  };

  const handleLoad = (sf) => {
    setActiveFilterId(sf.id);
    onLoadFilter(sf.filters);
    toast.success(`Filtro "${sf.name}" aplicado`);
  };

  const handleDelete = async (sfId) => {
    try {
      await axiosAuth().delete(`/saved-filters/${sfId}`);
      if (activeFilterId === sfId) setActiveFilterId(null);
      await loadSavedFilters();
      toast.success("Filtro eliminado");
    } catch { toast.error("Error al eliminar"); }
  };

  const openSaveDialog = () => {
    const active = savedFilters.find(sf => sf.id === activeFilterId);
    setFilterName(active ? active.name : "");
    setSaveDialog(true);
  };

  const hasActiveFilters = Object.values(currentFilters).some(v => v && v !== "");

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="saved-filters-bar">
      {savedFilters.map(sf => (
        <div key={sf.id} className={`inline-flex items-center gap-1 h-7 rounded-sm border text-[10px] font-semibold ${activeFilterId === sf.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"} transition-colors`}>
          <button onClick={() => handleLoad(sf)} className="pl-2 pr-1 h-full flex items-center gap-1" data-testid={`load-filter-${sf.id}`}>
            <FolderOpen className="w-3 h-3" /> {sf.name}
          </button>
          <button onClick={() => handleDelete(sf.id)} className="pr-1.5 h-full flex items-center opacity-40 hover:opacity-100 hover:text-red-500 transition-opacity" data-testid={`delete-filter-${sf.id}`}>
            <Trash2 className="w-2.5 h-2.5" />
          </button>
        </div>
      ))}
      {hasActiveFilters && (
        <Button variant="outline" size="sm" onClick={openSaveDialog} className="h-7 px-2 text-[10px] rounded-sm border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700 font-semibold" data-testid="save-filter-btn">
          <Bookmark className="w-3 h-3 mr-1" /> {activeFilterId ? "Actualizar filtro" : "Guardar filtro"}
        </Button>
      )}

      <Dialog open={saveDialog} onOpenChange={o => { if (!o) setSaveDialog(false); }}>
        <DialogContent className="max-w-xs rounded-sm" data-testid="save-filter-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold flex items-center gap-2">
              <Save className="w-4 h-4 text-slate-600" /> {activeFilterId ? "Actualizar filtro" : "Guardar filtro"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 pt-1">
              {activeFilterId ? "Actualiza el nombre o los filtros guardados." : "Dale un nombre descriptivo para encontrarlo facilmente."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Input value={filterName} onChange={e => setFilterName(e.target.value)} placeholder="Ej: Pendientes Madrid Q1"
              className="rounded-sm h-8 text-xs" data-testid="filter-name-input" />
          </div>
          <DialogFooter className="gap-1">
            <Button variant="outline" size="sm" onClick={() => setSaveDialog(false)} className="rounded-sm text-xs">Cancelar</Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !filterName.trim()} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs" data-testid="confirm-save-filter-btn">
              <Save className="w-3 h-3 mr-1" /> {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
